import UserFile from "../Models/UserFile";

/**
 * Service for interacting with user file API endpoints.
 */
class UserFileService {
    // Base API URL, safely stripped of trailing slashes
    private static readonly baseUrl: string = (() => {
        if (process.env.NODE_ENV !== "production") {
            return "https://localhost:5001/UserFiles";
        }

        const origin = window.location.origin.replace(/\/+$/, "");
        return `${origin}/api/UserFiles`;
    })();

    /**
     * Common fetch wrapper to handle headers and errors
     * @param url Relative endpoint URL
     * @param options Fetch options (method, headers, body, etc.)
     */
    private static async request<T>(url: string, options: RequestInit): Promise<T> {
        const response = await fetch(url, options);

        if (!response.ok) {
            const errorText = await response.text();
            const error = new Error(errorText || `Request failed with status ${response.status}`) as any;
            (error as any).status = response.status;
            throw error;
        }

        const contentType = response.headers.get("content-type");
        if (contentType?.includes("application/json")) {
            return response.json();
        } else {
            return response.blob() as unknown as T;
        }
    }

    /**
     * Fetch all user files
     * @param token Authorization token
     */
    static async getAll(token: string | null): Promise<UserFile[]> {
        const data = await this.request<any>(this.baseUrl, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Accept": "application/json"
            }
        });
        const items = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
        return items.map((item: any) => ({
            ...item,
            createdAt: new Date(item.createdAt),
            dueDate: item.dueDate ? new Date(item.dueDate) : null
        }));
    }

    /**
     * Fetch paginated user files
     */
    static async getPaged(token: string | null, page = 1, pageSize = 15): Promise<{ page: number; pageSize: number; totalItems: number; items: UserFile[]; }> {
        const data = await this.request<any>(`${this.baseUrl}/paged?page=${page}&pageSize=${pageSize}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Accept": "application/json"
            }
        });
        return {
            page: data.page,
            pageSize: data.pageSize,
            totalItems: data.totalItems,
            items: data.items.map((item: any) => ({
                ...item,
                createdAt: new Date(item.createdAt),
                dueDate: item.dueDate ? new Date(item.dueDate) : null
            })) as UserFile[]
        };
    }

    /**
     * Download a list of user files as a Blob
     * @param files Array of UserFile objects to download
     * @param token Authorization token
     */
    static async downloadFiles(files: UserFile[], token: string | null): Promise<Blob> {
        return this.request<Blob>(`${this.baseUrl}/download`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(files)
        });
    }

    /**
     * Change the status of a list of user files
     * @param files Array of UserFile objects
     * @param token Authorization token
     */
    static async changeFilesStatus(files: UserFile[], token: string | null): Promise<UserFile[]> {
        return this.request<UserFile[]>(`${this.baseUrl}/change_status`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(files)
        });
    }
}

export default UserFileService;
